import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Asset,
  ASSET_STATUS_BUYER_ABANDONED,
  ASSET_STATUS_CLOSED,
  ASSET_STATUS_DELETED,
  ASSET_STATUS_COLLAPSED_TO_INVENTORY,
  ASSET_STATUS_COLLAPSED_TO_SELLER,
  ASSET_STATUS_SOLD,
  RBA_LIVE,
  assetMessage,
  AssetSaleInfo
} from '../../../shared/domain/item';
import {MoveAssetsComponent} from '../move-assets.component';
import {DialogService, UserSessionService} from '@rbaip/ui-component';
import {MatDialog} from '@angular/material/dialog';
import {Opportunity} from '../../../shared/domain/opportunity';
import {permissionType, PermissionUtils} from '../../../shared/permission';
import {AssetPackageDto} from '../../../shared/domain/asset-package';
import {Subscription} from 'rxjs';
import {ItemSummaryItemSummaryService} from '../../../shared/asset/item-summary-item-summary.service';
import {ItemSummaryAssetsService} from '../../../shared/asset/item-summary-assets.service';
import {ItemSummaryItemService} from "../../../shared/asset";

@Component({
  selector: 'lib-item-summary-move-item-button',
  templateUrl: './move-item-button.component.html'
})
export class MoveItemButtonComponent implements OnInit, OnDestroy {
  @Input() asset: Asset;
  @Input() assetSaleInfo: AssetSaleInfo;
  @Input() opportunity: Opportunity;
  @Input() assetPackage: AssetPackageDto;
  @Input() itemSummaryService: ItemSummaryItemSummaryService;
  @Input('itemService') itemService: ItemSummaryItemService;
  @Input('isAuctionEventActive') isAuctionEventActive: boolean;
  @Input() isEventEndedMoreThan30DaysAgo: boolean;
  @Input() isEventEndedMoreThan120DaysAgo: boolean;
  private canMoveItem = false;
  private canMoveItemPostContract = false;
  private canMoveSoldRBAsset: boolean;
  private canMoveSoldIPAsset: boolean;
  private userChangedSubscription: Subscription;
  constructor(private dialogService: DialogService,
              private dialog: MatDialog,
              private assetsService: ItemSummaryAssetsService,
              private userSessionService: UserSessionService) {
  }

  ngOnInit(): void {
    this.subscribeUserChanged();
    this.init();
  }

  ngOnDestroy(): void {
    if (this.userChangedSubscription) {
      this.userChangedSubscription.unsubscribe();
    }
  }

  private init(): void {
    this.initPermission();
  }

  private subscribeUserChanged(): void {
    this.userChangedSubscription = this.userSessionService.userChanged
      .subscribe(user => {
        if (user) {
          this.init();
        }
      });
  }

  private initPermission(): void {
    this.canMoveItem = PermissionUtils.hasPermission(this.userSessionService, permissionType.canMoveAsset);
    this.canMoveItemPostContract = this.canMoveItem && PermissionUtils.hasPermission(this.userSessionService, permissionType.canCreateEditContractedAssets);
    this.canMoveSoldRBAsset = PermissionUtils.hasPermission(this.userSessionService, permissionType.canMoveSoldRBAsset);
    this.canMoveSoldIPAsset = PermissionUtils.hasPermission(this.userSessionService, permissionType.canMoveSoldIPAsset);
  }

  public isMoveAssetButtonVisible(): boolean {
    return !(this.assetPackage
      && this.assetPackage.isPackageInPricing())
      && this.asset
      && this.asset.assetStatusId !== ASSET_STATUS_DELETED
      && (!this.asset.equipId || this.canMoveItemPostContract
          || (this.asset.assetStatusId === ASSET_STATUS_SOLD && (this.canMoveSoldRBAsset || this.canMoveSoldIPAsset)));
  }

  public moveAsset(): void {
    if (this.asset
      && this.asset.equipId
      && this.asset.equipId !== 0
      && this.asset.auctionEventId) {
      this.dialogService.showProgress();
      this.assetsService.getComponentizedItems(this.asset.auctionEventId, [this.asset.equipId])
        .subscribe(theItems => {
        const assetIsChildComponent = !!theItems
          && theItems.length > 0
          && this.asset.equipId !== theItems[0].parentComponent;
        const assetIsParentComponent = !!theItems
          && theItems.length > 0
          && this.asset.equipId === theItems[0].parentComponent
          && theItems[0].childComponents
          && theItems[0].childComponents.length > 0;
        if (assetIsChildComponent) {
          this.dialogService.hideProgress();
          this.dialogService.showMessageDialog(assetMessage.parentComponentsNotSelectedForMoveError);
        } else if (assetIsParentComponent) {
          this.dialogService.showMessageDialog(assetMessage.parentComponentSelectedForMoveWarning)
            .afterClosed().subscribe(response => {
            this.assetsService.getAssetsFromEquipIds(theItems[0].childComponents.join(','))
              .subscribe(childComponentAssets => {
                const childItemsForMove: Asset[] = Asset.constructFromSearchListJson(childComponentAssets);
                const assetsToMove = [this.asset].concat(childItemsForMove);
                this.dialogService.hideProgress();
                doAssetMove.bind(this)(assetsToMove);
              }, error => {
                this.dialogService.hideProgress();
                this.dialogService.showMessage('Error while loading child component assets data.');
              });
          });
        } else {
          this.dialogService.hideProgress();
          doAssetMove.bind(this)([this.asset]);
        }
      });
    } else {
      doAssetMove.bind(this)([this.asset]);
    }
    function doAssetMove(assets: Asset[]) {
      this.dialog.open(MoveAssetsComponent, {
        panelClass: 'mat-panel--full-height',
        data: {
          selectedAssets: assets,
          contractId: this.opportunity?.sellerContractId,
          itemSummaryItemService: this.itemService,
          opportunity: this.opportunity,
          isAuctionEventActive : this.isAuctionEventActive,
          eventEndedMoreThan30DaysAgo : this.isEventEndedMoreThan30DaysAgo,
          eventEndedMoreThan120DaysAgo : this.isEventEndedMoreThan120DaysAgo,
          canMoveSoldRBAsset : this.canMoveSoldRBAsset,
          canMoveSoldIPAsset: this.canMoveSoldIPAsset
        },
        disableClose: true
      }).afterClosed().subscribe(response => {
        if ( response ) {
          this.reloadItem();
        }
      });
    }
  }

  public validateMoveAsset(): void {
    let hasSoldRBItems = false;
    let hasCollapsedToInventoryItems = false;
    let hasSoldIPItems = false;
    let hasItemsWithNoActions= false;
    if (this.asset.assetStatusId === ASSET_STATUS_SOLD) {
      if (this.asset.saleType === RBA_LIVE) {
        hasSoldRBItems = true;
      } else {
        hasSoldIPItems = true;
      }
    }

    if (this.asset.assetStatusId === ASSET_STATUS_CLOSED) {
      hasItemsWithNoActions = true;
    }

    if (this.asset.assetStatusId === ASSET_STATUS_COLLAPSED_TO_INVENTORY && this.asset.saleType === RBA_LIVE) {
      hasCollapsedToInventoryItems = true;
    }

    if (this.validateClosedAndRelistedItem(hasItemsWithNoActions)) {
      this.validateSoldAssets(hasSoldRBItems || hasSoldIPItems || hasCollapsedToInventoryItems);
    }
  };

  validateClosedAndRelistedItem(hasItemsWithNoActions) {
    if (hasItemsWithNoActions) {
      this.dialogService.showMessageDialog(assetMessage.assetMoveNoActionError);
      return false;
    }
    return true;
  }

  validateSoldAssets(hasSoldOrCollapsedToInventoryItems) {
    if (hasSoldOrCollapsedToInventoryItems) {
      this.dialogService.showMessageDialog(assetMessage.assetSoldStatusMoveWarning)
        .afterClosed().subscribe(response => {
        if (response) {
          this.moveAsset();  //If all required validations are completed, it shows the necessary options for move asset.
        }
      });
    } else {
      this.moveAsset();
    }
  }

  reloadItem(): void {
    this.itemSummaryService.getItem(this.asset.equipId ? this.asset.equipId : this.asset.assetId);
  }
}
